import { useState, useRef, useCallback, useEffect } from 'react';

export interface AudioPlayerState {
  isPlaying: boolean;
  error: string | null;
  duration: number;
  currentTime: number;
}

export function useAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  
  // Play audio from base64 data
  const playAudio = useCallback(async (audioData: string, audioFormat: string = 'mp3'): Promise<void> => {
    const audio = audioRef.current;
    
    if (!audio.paused) {
      audio.pause();
    }
    
    const mimeType = getMimeType(audioFormat);
    const audioUrl = `data:${mimeType};base64,${audioData}`;

    audio.src = audioUrl;
    audio.volume = 1.0;
    audio.muted = false; // Explicitly unmute

    audio.load();
    return audio.play().catch((err) => {
      console.error("Error during playback:", err);
      setError(`Playback failed: ${err.message}`);
      throw err;
    });
  }, []);

  // Stop audio playback
  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio.paused) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  // Get current audio info
  const getCurrentAudioInfo = useCallback(() => {
    if (audioRef.current) {
      return {
        element: audioRef.current,
        isPlaying,
        duration,
        currentTime,
        volume: 1.0
      };
    }
    
    return null;
  }, [isPlaying, duration, currentTime]);

  // Helper function to get MIME type
  const getMimeType = (audioFormat: string): string => {
    const formatMap: Record<string, string> = {
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'webm': 'audio/webm',
      'm4a': 'audio/mp4'
    };
    return formatMap[audioFormat.toLowerCase()] || 'audio/mpeg';
  };
  
  const unlockAudioContext = useCallback((): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      const audio = audioRef.current;
      if (audio.unlocked) {
        resolve();
        return;
      }
      
      const onPlay = () => {
        audio.pause();
        audio.unlocked = true;
        console.log("Audio context unlocked.");
        cleanup();
        resolve();
      };
      
      const onError = (e: Event) => {
        cleanup();
        const mediaError = (e.target as HTMLAudioElement).error;
        console.error('Audio context unlock failed:', mediaError);
        reject(new Error(mediaError?.message || 'Failed to unlock audio context'));
      };

      const cleanup = () => {
        audio.removeEventListener('play', onPlay);
        audio.removeEventListener('error', onError);
      };

      audio.addEventListener('play', onPlay, { once: true });
      audio.addEventListener('error', onError, { once: true });

      audio.src = `data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA`;
      audio.volume = 0; // Unlock with zero volume, will be set to 1 on play
      audio.muted = false;
      try {
        await audio.play();
      } catch (error) {
        cleanup();
        reject(error);
      }
    });
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, []);

  return {
    isPlaying,
    error,
    duration,
    currentTime,
    playAudio,
    stopAudio,
    getCurrentAudioInfo,
    unlockAudioContext
  };
}

declare global {
  interface HTMLAudioElement {
    unlocked?: boolean;
  }
}
